
import React from 'react';

interface SkeletonProps {
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => {
  return (
    <div className={`animate-pulse bg-paper-200/80 rounded-xl ${className}`}></div>
  );
};

export const DashboardSkeleton = () => {
    return (
        <div className="space-y-8 animate-fade-in pb-20 pt-2">
            {/* Header Skeleton */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 border-b border-line pb-4">
                <div>
                    <Skeleton className="h-8 w-64 mb-2" />
                    <Skeleton className="h-4 w-96" />
                </div>
                <Skeleton className="h-10 w-32 rounded-xl" />
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-5 rounded-2xl border border-line-soft shadow-sm h-40 flex flex-col justify-between">
                    <div className="flex justify-between">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-8 w-8 rounded-lg" />
                    </div>
                    <Skeleton className="h-10 w-32" />
                    <Skeleton className="h-2 w-full rounded-full" />
                </div>
                <div className="md:col-span-2 grid grid-cols-2 gap-4">
                    <div className="bg-white p-5 rounded-2xl border border-line-soft shadow-sm h-40 flex flex-col justify-center gap-4">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-8 w-16" />
                    </div>
                    <div className="bg-white p-5 rounded-2xl border border-line-soft shadow-sm h-40 flex flex-col justify-center gap-4">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-8 w-16" />
                    </div>
                </div>
            </div>

            {/* List Skeleton */}
            <div className="bg-white rounded-2xl border border-line-soft shadow-sm p-6 min-h-[400px]">
                <div className="flex gap-4 mb-6">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-6 w-24" />
                </div>
                <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-line-soft">
                            <div className="flex items-center gap-3">
                                <Skeleton className="h-4 w-4" />
                                <div>
                                    <Skeleton className="h-4 w-48 mb-1" />
                                    <Skeleton className="h-3 w-24" />
                                </div>
                            </div>
                            <Skeleton className="h-4 w-16" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- NEW PUBLIC SKELETONS ---

export const LandingSkeleton = () => {
    // Mörk hero (se /theme-init.js): skelettet ska se ut som heron som kommer,
    // inte som en ljus sida som sedan byts mot en mörk.
    if (document.documentElement.getAttribute('data-boot') === 'dark-hero') {
        return (
            <div className="min-h-screen bg-stone-900 overflow-hidden" aria-busy="true">
                <div className="flex items-center justify-between px-4 md:px-6 py-4 animate-pulse">
                    <div className="h-8 w-8 rounded-full bg-white/10"></div>
                    <div className="h-9 w-24 rounded-full bg-white/10"></div>
                </div>
            </div>
        );
    }
    return (
        <div className="min-h-screen bg-paper-50 animate-pulse overflow-hidden">
            {/* Navbar Placeholder */}
            <div className="h-20 w-full bg-white/50 border-b border-line-soft flex items-center justify-between px-6">
                <div className="h-8 w-32 bg-paper-200 rounded-lg"></div>
                <div className="h-8 w-24 bg-paper-200 rounded-full"></div>
            </div>

            {/* Hero Section */}
            <div className="relative h-[60vh] bg-paper-200 w-full flex items-center justify-center">
                <div className="text-center space-y-4 max-w-xl w-full px-6">
                    <div className="h-12 w-3/4 mx-auto bg-paper-300 rounded-xl"></div>
                    <div className="h-4 w-1/2 mx-auto bg-paper-300 rounded-lg"></div>
                    <div className="h-12 w-40 mx-auto bg-paper-300 rounded-full mt-6"></div>
                </div>
            </div>

            {/* Features / Content */}
            <div className="max-w-7xl mx-auto px-6 py-20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-paper-200"></div>
                            <div className="h-6 w-32 bg-paper-200 rounded-lg"></div>
                            <div className="h-20 w-full bg-paper-200 rounded-xl"></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const StoreSkeleton = () => {
    return (
        <div className="min-h-screen bg-paper-50 animate-pulse">
            {/* Header */}
            <div className="fixed top-0 left-0 right-0 h-20 bg-white/80 backdrop-blur-md z-40 flex items-center justify-between px-6 border-b border-line-soft">
                <div className="h-8 w-32 bg-paper-200 rounded-lg"></div>
                <div className="h-10 w-24 bg-paper-200 rounded-full"></div>
            </div>

            {/* Store Hero */}
            <div className="mt-0 h-[45vh] bg-paper-200 w-full relative">
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                    <div className="h-10 w-64 bg-paper-300/50 rounded-xl"></div>
                    <div className="h-4 w-48 bg-paper-300/50 rounded-lg"></div>
                </div>
            </div>

            {/* Product Grid Skeleton */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12 pb-20">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <ProductCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export const ProductCardSkeleton = () => {
    return (
        <div className="bg-white rounded-2xl md:rounded-3xl p-0 overflow-hidden border border-line-soft h-full flex flex-col">
            <div className="aspect-[4/3] md:aspect-square bg-paper-200 w-full relative">
                <div className="absolute top-3 right-3 w-12 h-6 bg-paper-300/50 rounded-lg"></div>
            </div>
            <div className="p-4 flex-1 flex flex-col gap-3">
                <div className="h-6 w-3/4 bg-paper-200 rounded-lg"></div>
                <div className="h-4 w-full bg-paper-100 rounded-lg"></div>
                <div className="mt-auto flex justify-between items-center pt-2">
                    <div className="h-4 w-16 bg-paper-200 rounded-lg"></div>
                    <div className="w-10 h-10 rounded-full bg-paper-200"></div>
                </div>
            </div>
        </div>
    );
};
